import * as microsoftTeams from '@microsoft/teams-js';
import { api } from './client';

export interface Attachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string;
}

export interface AttachmentUpload {
  /** Resolves with the created attachment row, rejects on failure or cancellation. */
  result: Promise<Attachment>;
  cancel: () => void;
}

/**
 * `fetch` has no upload-progress event, so per-file progress (wireframe R4a:
 * "62% · 3.1 MB of 5.0 MB") needs a raw `XMLHttpRequest` instead of the shared
 * `api()` helper.
 */
export function uploadAttachment(
  requestId: string,
  file: File,
  onProgress: (fraction: number) => void,
): AttachmentUpload {
  const xhr = new XMLHttpRequest();
  const result = new Promise<Attachment>((resolve, reject) => {
    void (async () => {
      let token: string;
      try {
        token = await microsoftTeams.authentication.getAuthToken();
      } catch {
        reject(new Error('Could not authenticate the upload'));
        return;
      }
      const form = new FormData();
      form.append('file', file);

      xhr.open('POST', `/api/attachments/${encodeURIComponent(requestId)}`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText) as Attachment);
        } else {
          reject(new Error(uploadErrorMessage(xhr)));
        }
      });
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed — check your connection'));
      });
      xhr.addEventListener('abort', () => {
        reject(new DOMException('Upload cancelled', 'AbortError'));
      });
      xhr.send(form);
    })();
  });
  return { result, cancel: () => xhr.abort() };
}

function uploadErrorMessage(xhr: XMLHttpRequest): string {
  try {
    const body = JSON.parse(xhr.responseText) as { error?: string };
    if (body.error) return body.error;
  } catch {
    // fall through to the generic message below
  }
  return 'Upload failed — check your connection';
}

/**
 * Wireframe D3 — called only once the client's own 10-second undo window has
 * already lapsed (or been superseded by a later removal), so this is always a
 * real, immediate delete of both the row and the blob.
 */
export const deleteAttachment = (id: string): Promise<void> =>
  api<void>(`/api/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' });

export interface AttachmentLimits {
  maxAttachmentSizeBytes: number;
  maxAttachmentsPerRequest: number;
}

/**
 * The live, administrator-configurable upload limits (#52, #258) —
 * `GET /api/attachments/limits`, ordinary authenticated access, read fresh
 * from `AppSetting` on every call, same as the upload route itself enforces.
 *
 * `AttachmentsSection` fetches this once on mount so its client-side
 * pre-check and dropzone hint text can't drift from what the backend
 * actually accepts. It falls back to the static defaults in
 * `lib/attachmentLimits.ts` on any failure — never blocks or breaks the
 * upload UI waiting on this, same resilience pattern used for the D365
 * master-data dropdowns elsewhere in this app.
 */
export const fetchAttachmentLimits = (): Promise<AttachmentLimits> =>
  api<AttachmentLimits>('/api/attachments/limits');

/**
 * Fetches the raw bytes for inline preview. The download route is
 * authenticated (it is the only place a download can be authorized against
 * the parent request), so a plain `<img src>` cannot reach it directly.
 */
export async function fetchAttachmentBlob(id: string): Promise<Blob> {
  const token = await microsoftTeams.authentication.getAuthToken();
  const res = await fetch(`/api/attachments/${encodeURIComponent(id)}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Could not load the file (${String(res.status)})`);
  return res.blob();
}
